import React, { useCallback, useEffect, useRef, useState } from "react";
import { useUrqlContext } from "providers/UrqlProvider";
import { useRequestsToLocalStorageManager } from "hooks/useRequestsToLocalStorageManager";
import { downloadFileByURL } from "utils/downloadFileByURL";
import { GetReportQry } from "api/queries";
import { useAuthStore } from "stores/authStore";
import { ProgressBars } from "./ProgressBars";
import {
  pollingRequestStatuses,
  SharedWorkerContext,
} from "./SharedWorkerContext";

export const SharedWorkerProvider = ({ children }) => {
  const isAuthenticated = useAuthStore.use.isAuthenticated();
  const [messages, setMessages] = useState(new Map());
  const [activeModal, setActiveModal] = useState(null);
  const workerRef = useRef(null);
  const { client } = useUrqlContext();
  const { getRequests, saveRequest, removeRequest } =
    useRequestsToLocalStorageManager();

  const sendMessage = useCallback((message) => {
    if (workerRef.current) {
      workerRef.current.port.postMessage(message);
    }
  }, []);

  const startPolling = useCallback(
    (qryNodeId, qryRequestId, modalParams) => {
      // TODO refactor, change to named props
      const interval = setInterval(() => {
        client
          .query(
            GetReportQry,
            {
              input: {
                requestId: qryRequestId,
                projectId: modalParams?.projectId,
              },
            },
            { requestPolicy: "network-only" },
          )
          .then(({ data, error }) => {
            if (error) {
              clearInterval(interval);
              setMessages((prev) => {
                const m = new Map(prev);
                m.set(qryNodeId, {
                  ...(m.get(qryNodeId) || {}),
                  status: pollingRequestStatuses.error,
                });
                return m;
              });
              sendMessage({
                type: "update",
                nodeId: qryNodeId,
                status: pollingRequestStatuses.error,
              });
              setTimeout(() => {
                sendMessage({ type: "remove", nodeId: qryNodeId });
                setMessages((prev) => {
                  const m = new Map(prev);
                  m.delete(qryNodeId);
                  return m;
                });
                removeRequest(qryRequestId);
              }, 5000);
              return;
            }
            if (
              data?.reportArchive.status === pollingRequestStatuses.inProgress
            ) {
              sendMessage({
                type: "update",
                requestId: qryRequestId,
                nodeId: qryNodeId,
              });
            }
            if (
              data?.reportArchive.status === pollingRequestStatuses.error ||
              data?.reportArchive.status ===
                pollingRequestStatuses.errorStatus2SMH
            ) {
              clearInterval(interval);
              sendMessage({
                type: "update",
                requestId: qryRequestId,
                nodeId: qryNodeId,
                status: pollingRequestStatuses.error,
              });
            }
            if (data?.reportArchive.status === pollingRequestStatuses.done) {
              // mark done locally immediately so UI updates without waiting for worker
              setMessages((prev) => {
                const m = new Map(prev);
                m.set(qryNodeId, {
                  ...(m.get(qryNodeId) || {}),
                  status: pollingRequestStatuses.done,
                  requestId: qryRequestId,
                });
                return m;
              });
              sendMessage({
                type: "update",
                requestId: qryRequestId,
                status: pollingRequestStatuses.done,
                nodeId: qryNodeId,
              });
              clearInterval(interval);
              removeRequest(qryRequestId);
              downloadFileByURL(data?.reportArchive.url);
              setTimeout(() => {
                sendMessage({
                  type: "remove",
                  nodeId: qryNodeId,
                });
                setMessages((prev) => {
                  const m = new Map(prev);
                  m.delete(qryNodeId);
                  return m;
                });
              }, 5000);

              setTimeout(() => {
                sendMessage({
                  type: "remove",
                  nodeId: qryNodeId,
                });
              }, 5000);
            }
          });
      }, 5000);

      // ensure UI shows the new polling entry immediately
      setMessages((prev) => {
        const newMessages = new Map(prev);
        newMessages.set(qryNodeId, {
          status: pollingRequestStatuses.inProgress,
          requestId: qryRequestId,
          modalParams,
          interval,
        });
        return newMessages;
      });

      sendMessage({
        type: "update",
        requestId: qryRequestId,
        nodeId: qryNodeId,
        status: pollingRequestStatuses.inProgress,
        modalParams,
        interval,
      });

      saveRequest(qryRequestId, {
        requestId: qryRequestId,
        nodeId: qryNodeId,
        status: pollingRequestStatuses.inProgress,
        modalParams,
      });
    },
    [client, removeRequest, saveRequest, sendMessage],
  );

  useEffect(() => {
    if (!window.SharedWorker) {
      console.error("SharedWorker isn't supported");
      return;
    }

    if (!workerRef.current) {
      try {
        workerRef.current = new SharedWorker("worker.js");
      } catch (error) {
        console.error("SharedWorker wasn't created", error);
        return;
      }
      workerRef.current.port.onmessage = (event) => {
        const { data } = event;

        if (data.type === "stateRemove") {
          setMessages((prevState) => {
            const newMessages = new Map(prevState);
            newMessages.delete(data.data.nodeId);
            return newMessages;
          });
          return;
        }

        if (data.type === "fullState") {
          if (data.data.length === 0) {
            const localStorageRequestsArray = Object.entries(getRequests());

            if (localStorageRequestsArray.length > 0 && isAuthenticated) {
              localStorageRequestsArray.forEach(([requestId, requestData]) => {
                startPolling(
                  requestData.nodeId,
                  requestId,
                  requestData.modalParams,
                );
              });
            }
          }
          setMessages(new Map(data.data));
          return;
        }

        if (data?.data?.nodeId) {
          setMessages((prevState) => {
            const newMessages = new Map(prevState);
            const { nodeId, status, requestId, modalParams, interval } =
              data.data;
            const existingMessage = newMessages.get(nodeId);

            const isSameState = existingMessage?.status === status;

            if (isSameState) {
              return prevState;
            }

            newMessages.set(nodeId, {
              status: status || existingMessage?.status,
              requestId: requestId || existingMessage?.requestId,
              modalParams: modalParams || existingMessage?.modalParams,
              interval: interval || existingMessage?.interval,
            });

            return newMessages;
          });
          return;
        }
        console.error("Invalid message from SharedWorker:", data);
      };

      workerRef.current.port.postMessage({ type: "getState" });

      workerRef.current.port.start();

      workerRef.current.onerror = (error) => {
        console.error("Error SharedWorker:", error);
      };
    }

    return () => {
      if (workerRef.current) {
        workerRef.current.port.close();
      }
    };
  }, [getRequests, isAuthenticated, startPolling]);

  const terminateRequest = useCallback(
    ({ interval, nodeId, requestId }) => {
      clearInterval(interval);
      removeRequest(requestId);
      // remove locally immediately so ProgressBars stops
      setMessages((prev) => {
        const m = new Map(prev);
        m.delete(nodeId);
        return m;
      });
      sendMessage({
        type: "remove",
        nodeId,
      });
    },
    [removeRequest, sendMessage],
  );

  const getMessageByNodeId = useCallback(
    (nodeId) => messages.get(nodeId),
    [messages],
  );

  return (
    <SharedWorkerContext.Provider
      value={{
        messages,
        activeModal,
        setActiveModal,
        getMessageByNodeId,
        startPolling,
        terminateRequest,
      }}
    >
      {children}
      {messages.size > 0 && <ProgressBars messages={messages} />}
    </SharedWorkerContext.Provider>
  );
};
